import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "@/shared/utils/getErrorMessage";
import { departmentGradesService } from "../services/departmentGrades.service";
import type { DepartmentGradeFormValues } from "../types";

const QUERY_KEY = ["department-grades"];

/**
 * `GET /department-grades` sahifalanmagan — `success()` butun massivni
 * qaytaradi (`positions`ning `usePositionsList`i bilan bir xil naqsh),
 * shuning uchun oddiy `useQuery` ishlatiladi.
 *
 * ⚠️ `enabled` — `department-grades:read` alohida ruxsat (urug'langan
 * `manager` rolida YO'Q). Doim mount qilinadigan chaqiruvchi (masalan
 * `DepartmentFormModal`) uni faqat haqiqatan kerak bo'lganda yoqadi, aks
 * holda har sahifa yuklanishida ovozsiz 403 bo'lardi.
 */
export function useDepartmentGradesList(options: { enabled?: boolean } = {}) {
	return useQuery({
		queryKey: QUERY_KEY,
		queryFn: () => departmentGradesService.list(),
		enabled: options.enabled ?? true,
	});
}

function useDepartmentGradesMutation<TVariables>(
	mutationFn: (variables: TVariables) => Promise<unknown>,
	successMessage: string,
	errorFallback: string
) {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: QUERY_KEY });
			message.success(successMessage);
		},
		onError: (error) => {
			message.error(getErrorMessage(error, errorFallback));
		},
	});
}

export function useCreateDepartmentGrade() {
	const { t } = useTranslation("positions");
	return useDepartmentGradesMutation(
		(input: DepartmentGradeFormValues) => departmentGradesService.create(input),
		t("grades.messages.created"),
		t("grades.messages.createFailed")
	);
}

export function useUpdateDepartmentGrade() {
	const { t } = useTranslation("positions");
	return useDepartmentGradesMutation(
		(vars: { id: string; input: Partial<DepartmentGradeFormValues> }) =>
			departmentGradesService.update(vars.id, vars.input),
		t("grades.messages.updated"),
		t("grades.messages.updateFailed")
	);
}

export function useDeleteDepartmentGrade() {
	const { t } = useTranslation("positions");
	return useDepartmentGradesMutation(
		(id: string) => departmentGradesService.remove(id),
		t("grades.messages.deleted"),
		t("grades.messages.actionFailed")
	);
}

export function useSetDepartmentGradePositions() {
	const { t } = useTranslation("positions");
	return useDepartmentGradesMutation(
		(vars: { id: string; positionIds: string[] }) =>
			departmentGradesService.setPositions(vars.id, vars.positionIds),
		t("grades.messages.positionsUpdated"),
		t("grades.messages.positionsUpdateFailed")
	);
}
